// The guest-registration field vocabulary, and how a capture surface asks for
// each field. Deliberately zod-free and free of value imports from `./enums`:
// the guest portal's check-in form renders from this module in the browser,
// and test/guest-client-zod.test.ts explains what classic zod would cost there.

import countries from "i18n-iso-countries";
import { alpha3ToAlpha2 } from "./country-name";
import type { documentTypeValues, sexValues, stayPurposeValues } from "./enums";

/**
 * The controlled set of data points a guest-registration filing can require.
 * Guest-scoped fields mirror `RegistrationData` / the `guests` columns;
 * `arrivalDate` / `departureDate` are reservation-scoped.
 *
 * The order is the order a form asks in, so a field another depends on comes
 * first: the country of birth before the place of birth it can unlock.
 */
export const registrationFieldValues = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "sex",
  "nationality",
  "documentType",
  "documentNumber",
  "personalNumber",
  "expiryDate",
  "issuingCountry",
  "residenceCountry",
  "residenceAddress",
  "birthCountry",
  "birthPlace",
  "stayPurpose",
  "arrivalDate",
  "departureDate",
] as const;
export type RegistrationField = (typeof registrationFieldValues)[number];

/** Fields the reservation supplies. No guest capture ever asks for them. */
const RESERVATION_SCOPED_FIELDS = new Set<RegistrationField>([
  "arrivalDate",
  "departureDate",
]);

/** A field a person enters (or a scan reads) for one guest. */
export type CapturedRegistrationField = Exclude<
  RegistrationField,
  "arrivalDate" | "departureDate"
>;

export function isCapturedField(
  field: RegistrationField
): field is CapturedRegistrationField {
  return !RESERVATION_SCOPED_FIELDS.has(field);
}

/** Host- and guest-facing name of each field. */
export const REGISTRATION_FIELD_LABELS: Record<RegistrationField, string> = {
  arrivalDate: "Arrival date",
  birthCountry: "Country of birth",
  birthPlace: "Place of birth",
  dateOfBirth: "Date of birth",
  departureDate: "Departure date",
  documentNumber: "Document number",
  documentType: "Document type",
  expiryDate: "Document expiry",
  firstName: "First name",
  issuingCountry: "Issuing country",
  lastName: "Last name",
  nationality: "Nationality",
  personalNumber: "Personal/ID number",
  residenceAddress: "Residence address",
  residenceCountry: "Country of residence",
  sex: "Sex",
  stayPurpose: "Purpose of stay",
};

export interface FieldOption<V extends string = string> {
  label: string;
  value: V;
}

export const DOCUMENT_TYPES = [
  { label: "Passport", value: "passport" },
  { label: "ID Card", value: "id_card" },
  { label: "Other", value: "other" },
] as const satisfies readonly FieldOption<
  (typeof documentTypeValues)[number]
>[];

export const SEX_OPTIONS = [
  { label: "Male", value: "M" },
  { label: "Female", value: "F" },
] as const satisfies readonly FieldOption<(typeof sexValues)[number]>[];

const STAY_PURPOSE_OPTIONS = [
  { label: "Tourism", value: "tourism" },
  { label: "Business", value: "business" },
  { label: "Education", value: "education" },
  { label: "Medical", value: "medical" },
  { label: "Family", value: "family" },
  { label: "Other", value: "other" },
] as const satisfies readonly FieldOption<(typeof stayPurposeValues)[number]>[];

/**
 * How a field is entered. Country fields name the ISO form the stored value
 * takes: MRZ-read `nationality` / `issuingCountry` are alpha-3, the
 * registration extension's `birthCountry` / `residenceCountry` are alpha-2.
 */
export type RegistrationFieldInput =
  | { kind: "text" }
  | { kind: "date" }
  | { kind: "country"; code: "alpha2" | "alpha3" }
  | { kind: "select"; options: readonly FieldOption[] };

/**
 * One entry per captured field, which is what lets a capture surface render
 * any field a regime asks for without knowing which regime asked.
 */
export const REGISTRATION_FIELD_INPUTS = {
  birthCountry: { code: "alpha2", kind: "country" },
  birthPlace: { kind: "text" },
  dateOfBirth: { kind: "date" },
  documentNumber: { kind: "text" },
  documentType: { kind: "select", options: DOCUMENT_TYPES },
  expiryDate: { kind: "date" },
  firstName: { kind: "text" },
  issuingCountry: { code: "alpha3", kind: "country" },
  lastName: { kind: "text" },
  nationality: { code: "alpha3", kind: "country" },
  personalNumber: { kind: "text" },
  residenceAddress: { kind: "text" },
  residenceCountry: { code: "alpha2", kind: "country" },
  sex: { kind: "select", options: SEX_OPTIONS },
  stayPurpose: { kind: "select", options: STAY_PURPOSE_OPTIONS },
  // `satisfies`, not an annotation: a surface that names one field reads the
  // ISO form off this table (`REGISTRATION_FIELD_INPUTS.nationality.code`)
  // instead of repeating it, which an annotation would widen away.
} satisfies Record<CapturedRegistrationField, RegistrationFieldInput>;

/** Regional-indicator offset: 'A' (0x41) maps to U+1F1E6. */
const REGIONAL_INDICATOR_OFFSET = 0x1_f1_e6 - 0x41;

/** Exactly two letters: anything else has no flag and must not be encoded. */
const ALPHA2_PATTERN = /^[A-Za-z]{2}$/;

/**
 * The flag emoji for an ISO 3166-1 alpha-2 code, or `null` for anything that
 * is not one, so a caller renders nothing rather than a tofu box. The codeset
 * is in the name: a guest's `nationality` is alpha-3, and passing one here
 * would lose the flag with nothing to say so.
 *
 * Emoji, not SVG: a picker names every ISO country, and 250 inlined flags
 * would cost a guest on a hotel Wi-Fi more than the flags are worth. The
 * platforms guests check in from — iOS, Android, macOS — draw them; Windows
 * draws the letter pair instead, which sits beside the country's own name
 * and so still reads.
 */
export function alpha2FlagEmoji(
  alpha2: string | null | undefined
): string | null {
  // Letters, not just length: two characters above the regional-indicator
  // range make `String.fromCodePoint` throw rather than return a glyph.
  if (!(alpha2 && ALPHA2_PATTERN.test(alpha2))) {
    return null;
  }
  return String.fromCodePoint(
    ...[...alpha2.toUpperCase()].map(
      (c) => c.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET
    )
  );
}

/**
 * The languages a country can be searched for by name, whichever one the
 * surface is currently showing. A German guest checking in through an
 * English portal types "Deutschland", and a list that only knows "Germany"
 * dead-ends them at the door with nothing to try next.
 *
 * It mirrors the guest portal's own locales; `apps/guest/src/i18n/messages.test.ts`
 * fails if the portal gains a language this list does not have.
 */
export const COUNTRY_SEARCH_LOCALES = ["en", "de", "fr", "it", "sl"] as const;

/**
 * A country as a picker offers it. `flag` is separate from `label` so a
 * surface can place it, size it or leave it out; the stored value is still
 * `value`, the ISO code the field declares.
 */
export interface CountryOption extends FieldOption {
  /**
   * Both ISO codes, whichever one this field stores. A guest reads one code
   * off their passport and does not know which codeset the field behind the
   * form happens to want.
   */
  codes: readonly string[];
  flag: string | null;
  /**
   * The country's name in every language the portal offers, so the search
   * finds it whatever language the reader is being shown.
   */
  names: readonly string[];
}

const countryOptionCache = new Map<string, CountryOption[]>();

/**
 * Every ISO country as a picker option, named in `locale` and sorted the way
 * that language sorts: a list of French names in English order is one a
 * French reader cannot scan.
 */
export function countryOptions(
  code: "alpha2" | "alpha3",
  locale = "en"
): CountryOption[] {
  const cacheKey = `${locale}:${code}`;
  const cached = countryOptionCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const names = new Intl.DisplayNames([locale], { type: "region" });
  const alsoKnownAs = COUNTRY_SEARCH_LOCALES.map(
    (other) => new Intl.DisplayNames([other], { type: "region" })
  );
  const collator = new Intl.Collator(locale);
  const options = Object.entries(countries.getAlpha2Codes())
    .map(([alpha2, alpha3]) => {
      const label = names.of(alpha2) ?? alpha2;
      return {
        codes: [alpha2, alpha3],
        flag: alpha2FlagEmoji(alpha2),
        label,
        names: [
          ...new Set([
            label,
            ...alsoKnownAs.map((it) => it.of(alpha2) ?? label),
          ]),
        ],
        value: code === "alpha2" ? alpha2 : alpha3,
      };
    })
    .sort((a, b) => collator.compare(a.label, b.label));
  countryOptionCache.set(cacheKey, options);
  return options;
}

/**
 * The option a stored country code reads as: its own, or for an MRZ code an
 * alpha-3 field holds outside the list (a passport's `D`) the country it names
 * under the code as stored, and as itself otherwise (`XXA`, or a code in the
 * other codeset). A control never blanks a record it cannot name.
 */
export function countryOptionFor(
  value: string,
  code: "alpha2" | "alpha3",
  locale = "en"
): CountryOption {
  const options = countryOptions(code, locale);
  const own = options.find((option) => option.value === value);
  if (own) {
    return own;
  }
  const alpha2 = code === "alpha3" ? alpha3ToAlpha2(value) : null;
  const named = alpha2 && options.find((option) => option.codes[0] === alpha2);
  return named
    ? { ...named, value }
    : { codes: [value], flag: null, label: value, names: [value], value };
}

/**
 * One field a regime requires. `onlyWhen` narrows it to the guests another
 * captured field describes: Italy wants a place of birth only of someone born
 * in Italy, because only then does it resolve to a municipality code.
 * `oneOf` names the only values the system can file, so any other answer
 * still counts as missing: Italy's police take a passport or an ID card,
 * never "Other".
 */
export interface RegistrationFieldRequirement {
  field: RegistrationField;
  oneOf?: readonly string[];
  onlyWhen?: { field: CapturedRegistrationField; equals: string };
}

/**
 * Two lists' demands on one field, as one demand holding the stricter of
 * each: an unconditional requirement beats a narrowed one, and a value set,
 * once any list names it, holds.
 */
function combine(
  a: RegistrationFieldRequirement,
  b: RegistrationFieldRequirement
): RegistrationFieldRequirement {
  const onlyWhen = a.onlyWhen && b.onlyWhen ? a.onlyWhen : undefined;
  const oneOf = a.oneOf ?? b.oneOf;
  return {
    field: a.field,
    ...(oneOf ? { oneOf } : {}),
    ...(onlyWhen ? { onlyWhen } : {}),
  };
}

/**
 * What every capture asks, whatever the regime: a guest is recorded by name.
 * A capture surface merges it into the regime's list.
 */
export const NAME_REQUIREMENTS: readonly RegistrationFieldRequirement[] = [
  { field: "firstName" },
  { field: "lastName" },
];

/** Union several requirement lists into one, in vocabulary order. */
export function mergeRequirements(
  ...lists: readonly (readonly RegistrationFieldRequirement[])[]
): RegistrationFieldRequirement[] {
  const byField = new Map<RegistrationField, RegistrationFieldRequirement>();
  for (const list of lists) {
    for (const requirement of list) {
      const existing = byField.get(requirement.field);
      byField.set(
        requirement.field,
        existing ? combine(existing, requirement) : requirement
      );
    }
  }
  return registrationFieldValues.flatMap((field) => {
    const requirement = byField.get(field);
    return requirement ? [requirement] : [];
  });
}

/** Whether a value answers a requirement in a form the system can file. */
export function isAcceptedValue(
  requirement: RegistrationFieldRequirement,
  value: string
): boolean {
  const trimmed = value.trim();
  const { oneOf } = requirement;
  return trimmed !== "" && (!oneOf || oneOf.includes(trimmed));
}

/**
 * What a form says under a required field left blank. One word: on an empty
 * form every field carries it, and the form already says why it asks.
 */
export const REQUIRED_FIELD_MESSAGE = "Required";

/**
 * What stops a value from answering a requirement, in words for the form, or
 * null when it can be filed. A value outside the system's set names the ones
 * it takes, so "Other" at an Italian property reads "Choose Passport or ID
 * Card."
 */
export function requirementProblem(
  requirement: RegistrationFieldRequirement,
  value: string
): string | null {
  if (isAcceptedValue(requirement, value)) {
    return null;
  }
  const { field, oneOf } = requirement;
  const input = isCapturedField(field)
    ? REGISTRATION_FIELD_INPUTS[field]
    : undefined;
  const isOutsideSet =
    value.trim() !== "" && oneOf !== undefined && input?.kind === "select";
  if (!isOutsideSet) {
    return REQUIRED_FIELD_MESSAGE;
  }
  const names = oneOf.map(
    (accepted) =>
      input.options.find((option) => option.value === accepted)?.label ??
      accepted
  );
  const choices = new Intl.ListFormat("en", { type: "disjunction" });
  return `Choose ${choices.format(names)}.`;
}

/** Whether a requirement applies to the guest these values describe. */
export function isRequirementActive(
  requirement: RegistrationFieldRequirement,
  values: Partial<Record<CapturedRegistrationField, string | undefined>>
): boolean {
  const { onlyWhen } = requirement;
  if (!onlyWhen) {
    return true;
  }
  const value = values[onlyWhen.field] ?? "";
  return value.toUpperCase() === onlyWhen.equals.toUpperCase();
}
